import React, { CSSProperties, HTMLProps } from "react";
import { Icon } from "@blueprintjs/core";
import styled from "styled-components";
import { darken, animation } from "polished";

type ButtonIntent = "primary" | "danger" | "ordinary";

const backgroundIntentColors = {
  primary: "#00A1E0",
  danger: "#c12f2f",
  ordinary: "#f5f8fa",
  fallback: "#f5f8fa",
};

const StyledBtn = styled.button<{
  isDisabled?: boolean;
  intent?: ButtonIntent;
  isLoading?: boolean;
}>`
  border-radius:50px!important;
  background:#00A1E0;
  color:#fff;
  font-family: 'Roboto';
  font-weight:bold;
  letter-spacing: 1px;
  font-size:21px;
  padding:5px 20px;
  text-align: center;
  text-transform:uppercase;
  text-decoration:none;
  margin:20px auto;
  color: ${props =>
    props.intent === "ordinary" ||
      (props.intent && !backgroundIntentColors[props.intent])
      ? "rgba(0,0,0,.8)"
      : "#fff"};
  background-color: ${props =>
    props.intent
      ? backgroundIntentColors[props.intent]
      : backgroundIntentColors.primary};
  border-radius: 5px;
  border: ${props =>
    props.intent === "ordinary" ||
      (props.intent && !backgroundIntentColors[props.intent])
      ? "1px solid rgba(0,0,0,.1)"
      : "none"};
  opacity: ${props => (props.isDisabled || props.isLoading ? 0.6 : 1)};
  cursor: ${props =>
    props.isDisabled || props.isLoading ? "not-allowed" : "pointer"};
  transition: all 0.2s ease-out;
  :hover {
    background-color: ${props =>
    props.isDisabled || props.isLoading
      ? props.intent
        ? backgroundIntentColors[props.intent] ||
        backgroundIntentColors.fallback
        : backgroundIntentColors.primary
      : darken(
        0.07,
        props.intent
          ? backgroundIntentColors[props.intent] ||
          backgroundIntentColors.fallback
          : backgroundIntentColors.primary
      )};
  }
`;

const StyledBtnLink = styled.a<{
  isDisabled?: boolean;
  intent?: ButtonIntent;
  isLoading?: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 7px 15px;
  height: max-content;
  width: 100%;
  color: ${props =>
    props.intent === "ordinary" ||
      (props.intent && !backgroundIntentColors[props.intent])
      ? "rgba(0,0,0,.8)"
      : "#fff"};
  background-color: ${props =>
    props.intent
      ? backgroundIntentColors[props.intent]
      : backgroundIntentColors.primary};
  border-radius: 5px;
  border: ${props =>
    props.intent === "ordinary" ||
      (props.intent && !backgroundIntentColors[props.intent])
      ? "1px solid rgba(0,0,0,.1)"
      : "none"};
  text-decoration: none;
  opacity: ${props => (props.isDisabled || props.isLoading ? 0.6 : 1)};
  cursor: ${props =>
    props.isDisabled || props.isLoading ? "not-allowed" : "pointer"};
  transition: all 0.2s ease-out;
  :hover {
    text-decoration: none;
    background-color: ${props =>
    props.isDisabled || props.isLoading
      ? props.intent
        ? backgroundIntentColors[props.intent] ||
        backgroundIntentColors.fallback
        : backgroundIntentColors.primary
      : darken(
        0.07,
        props.intent
          ? backgroundIntentColors[props.intent] ||
          backgroundIntentColors.fallback
          : backgroundIntentColors.primary
      )};
  }
`;

export const StyledSpinner = styled(Icon)`
  margin-left: 10px;
  ${animation("rotate-center .8s linear infinite both")};
`;

interface IProps extends HTMLProps<HTMLButtonElement> {
  isLoading?: boolean;
  children: React.ReactNode;
  style?: CSSProperties;
  isDisabled?: boolean;
  intent?: ButtonIntent;
  loadingSpinnerClassName?: string;
  loadingSpinnerStyles?: CSSProperties;
}

export default ({
  children,
  isLoading,
  loadingSpinnerClassName,
  loadingSpinnerStyles,
  href,
  ...rest
}: IProps) => {
  const externalSpinnerStyles = loadingSpinnerStyles || {};

  return (
    href ? (
      <StyledBtnLink isLoading={isLoading} {...rest} href={href} >
        {children}
        {isLoading ? (
          <StyledSpinner icon="repeat" style={externalSpinnerStyles} />
        ) : null
        }
      </StyledBtnLink >
    ) : (
        <StyledBtn isLoading={isLoading} {...rest}>
          {children}
          {isLoading ? (
            <StyledSpinner icon="repeat" style={externalSpinnerStyles} />
          ) : null}
        </StyledBtn>
      )
  );
};
